import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { THEME_OPTIONS } from '../../lib/theme';
import { useConfirm } from '../ui/ConfirmDialog';
import { DropdownMenu, type MenuItem } from '../ui/DropdownMenu';
import { ChevronDown, Monitor, Moon, Sun, User } from '../ui/icons';

const THEME_ICONS = { system: <Monitor />, light: <Sun />, dark: <Moon /> } as const;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Account menu in the top bar: Profile, Theme (System / Light / Dark), Sign out. */
export function UserMenu() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const confirm = useConfirm();
  const name = user?.displayName || user?.email || 'Account';

  const items: MenuItem[] = [
    { label: 'Profile', to: '/profile', icon: <User /> },
    { separator: true },
    { heading: 'Theme' },
    ...THEME_OPTIONS.map((o) => ({
      label: o.label,
      icon: THEME_ICONS[o.value],
      checked: theme === o.value,
      onSelect: () => setTheme(o.value),
    })),
    { separator: true },
    {
      label: 'Sign out',
      danger: true,
      onSelect: () => {
        // Let the menu close first so the dialog's focus handling is not fighting it.
        setTimeout(async () => {
          if (await confirm({ title: 'Sign out?', description: 'You will need to sign in again to continue.', confirmLabel: 'Sign out' })) {
            logout();
          }
        }, 0);
      },
    },
  ];

  return (
    <DropdownMenu
      align="end"
      items={items}
      trigger={
        <button
          type="button"
          aria-label={`Account menu for ${name}`}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <span
            aria-hidden="true"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/50 dark:text-primary-200"
          >
            {initials(name) || '?'}
          </span>
          <span className="hidden max-w-[10rem] truncate sm:inline">{name}</span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>
      }
    />
  );
}
